// src/components/FormPedido.jsx
import { useEffect, useMemo, useState, useCallback, useContext } from "react";
import Select from "react-select";
import ContextStates from "../context/ContextStates";
import { useNavigate } from 'react-router-dom';
import { Url } from "./Url";

// =============== Helpers ===============
const num = (v) => {
    if (typeof v === "number") return v;
    const cleaned = String(v ?? "")
        .replace(/[^\d,.-]/g, "")
        .replace(/\./g, "")
        .replace(",", ".");
    const n = parseFloat(cleaned);
    return isNaN(n) ? 0 : n;
};

const formatARS = (n) =>
    Number(n).toLocaleString("es-AR", {
        style: "currency",
        currency: "ARS",
        maximumFractionDigits: 2,
    });

const clampByStock = (item, desiredQty) => {
    const stock = Number(item.stock ?? Infinity);
    const safeStock = Number.isFinite(stock) ? Math.max(0, Math.floor(stock)) : Infinity;
    const qty = Math.max(0, Math.floor(Number(desiredQty) || 0));
    return Math.min(qty, safeStock);
};

// Suma cantidades por numerocombo
const buildQtyByCombo = (items) => {
    const map = {};
    for (const it of items || []) {
        const nc = it?.numerocombo;
        if (!nc) continue;
        const q = Number(it.cantidad) || 0;
        map[nc] = (map[nc] || 0) + q;
    }
    return map;
};

// Dado un producto, usa sus escalones (precios_combo) y la cantidad TOTAL del combo
const unitFromProductComboTiers = (base, productComboTiers, groupQty) => {
    let unit = Number(base) || 0;
    const tiers = Array.isArray(productComboTiers)
        ? productComboTiers.slice().sort((a, b) => a.cantidadmin - b.cantidadmin)
        : [];
    for (const t of tiers) {
        if (groupQty >= Number(t.cantidadmin)) unit = Number(t.precio_unitario) || 0;
        else break;
    }
    return unit;
};

// Precio unitario final SOLO por combo (sin promos de otro tipo)
const unitByCombo = (item, selfQty, qtyByCombo) => {
    const base = Number(item.precio ?? item.precio_base ?? 0) || 0;
    if (item.numerocombo && item.precios_combo?.length) {
        const groupQty = qtyByCombo[item.numerocombo] || 0;
        return unitFromProductComboTiers(base, item.precios_combo, groupQty);
    }
    return base;
};

const comisionFromProductComboTiers = (base, productComboTiers, groupQty) => {
    let comision = Number(base) || 0;
    const tiers = Array.isArray(productComboTiers)
        ? productComboTiers.slice().sort((a, b) => a.cantidadmin - b.cantidadmin)
        : [];
    for (const t of tiers) {
        if (groupQty >= Number(t.cantidadmin)) comision = Number(t.comision) || 0;
        else break;
    }
    return comision;
};

const comisionByCombo = (item, selfQty, qtyByCombo) => {
    const base = Number(item.comision) || 0;
    if (item.numerocombo && item.precios_combo?.length) {
        const groupQty = qtyByCombo[item.numerocombo] || 0;
        return comisionFromProductComboTiers(base, item.precios_combo, groupQty);
    }
    return base;
};

const subtotalByCombo = (item, qty, qtyByCombo) => {
    const q = Number(qty) || 0;
    if (q <= 0) return 0;
    const unit = unitByCombo(item, q, qtyByCombo);
    return unit * q;
};

// Hint: muestra cuánto falta para el próximo escalón del PRODUCTO (sumando cantidades del combo)
const nextComboHint = (item, qtyByCombo) => {
    if (!(item.numerocombo && item.precios_combo?.length)) return null;
    const currentGroupQty = qtyByCombo[item.numerocombo] || 0;
    const tiers = item.precios_combo.slice().sort((a, b) => a.cantidadmin - b.cantidadmin);
    for (const t of tiers) {
        const min = Number(t.cantidadmin);
        if (min > currentGroupQty) {
            return {
                faltan: min - currentGroupQty,
                precio: Number(t.precio_unitario) || 0,
            };
        }
    }
    return null;
};

// =============== Componente ===============
export default function FormPedido({ onResumen }) {
    const {
        // contexto global
        clientes,
        setClientes,
        clienteSelected,
        setClienteSelected,
        productos = [],
        setProductos = () => { },
        productosSeleccionados = [],
        setProductosSeleccionados = () => { },
    } = useContext(ContextStates);

    const [loading, setLoading] = useState(false);
    const [cliente, setCliente] = useState(clienteSelected || { nombre: "—" });
    const [prefilled, setPrefilled] = useState(false); // evita re-precargar
    const [customPrices, setCustomPrices] = useState({}); // { productId: customPrice }
    const navigate = useNavigate();
    // 1) Traer catálogo y normalizar (incluye numerocombo + precios_combo)
    useEffect(() => {
        const fetchProductos = async () => {
            setLoading(true);
            try {
                const form = new FormData();
                form.append("tipo", "1");
                const res = await fetch(Url + "api/listadoproductos.php", { method: "POST", body: form });
                const data = await res.json();
                const arr = Array.isArray(data?.results) ? data.results : [];
                const normalized = arr.map((it) => ({
                    ...it,
                    precio: num(it.precio),
                    comision: num(it.comision),
                    preciocosto: num(it.preciocosto),
                    stock: Number(it.stock ?? 0),
                    numerocombo: it.numerocombo ?? null,
                    precios_combo: Array.isArray(it.precios_combo)
                        ? it.precios_combo
                            .map((t) => ({
                                cantidadmin: Number(t.cantidadmin),
                                precio_unitario: num(t.precio_unitario),
                                comision: num(t.comision),
                            }))
                            .sort((a, b) => a.cantidadmin - b.cantidadmin)
                        : [],
                }));
                setProductos(normalized);
            } catch (e) {
                console.error(e);
            } finally {
                setLoading(false);
            }
        };
        fetchProductos();
    }, [setProductos]);

    // 2) Traer clientes (si hace falta)
    useEffect(() => {
        const consultaClientes = async () => {
            const formData = new FormData();
            formData.append("idusuario", "1");
            formData.append("filtro", "");
            formData.append("busqueda", "");
            try {
                const response = await fetch(Url + "api/listClientes.php", { method: "POST", body: formData });
                const data = await response.json();
                setClientes(Array.isArray(data?.results) ? data.results : []);
            } catch (e) {
                console.log(e);
            }
        };
        consultaClientes();
    }, [setClientes]);

    // 3) Precarga para edición (si marker === 1) — corre SOLO cuando hay catálogo y todavía no prellenamos
    useEffect(() => {
        if (prefilled) return;
        if (Number(clienteSelected?.marker) !== 1) return;

        const pedido = Array.isArray(clienteSelected?.pedido) ? clienteSelected.pedido : [];
        if (!pedido.length) return;
        if (!Array.isArray(productos) || productos.length === 0) return;

        // agrupar por idproducto (por si hay líneas repetidas)
        const qtyByProduct = pedido.reduce((acc, li) => {
            const idp = Number(li.idproducto ?? li.id ?? 0);
            const q = Number(li.cantidad) || 0;
            if (!idp || q <= 0) return acc;
            acc[idp] = (acc[idp] || 0) + q;
            return acc;
        }, {});

        const selected = Object.entries(qtyByProduct)
            .map(([idStr, qty]) => {
                const id = Number(idStr);
                const prod = productos.find((p) => Number(p.id) === id);
                if (!prod) {
                    console.warn("[Pedido] Producto del pedido no está en catálogo:", { id, qty });
                    return null;
                }
                const stock = Number(prod.stock ?? 0);
                const cantidad = Math.min(Math.max(0, Math.floor(qty)), Math.max(0, Math.floor(stock)));
                return { ...prod, cantidad };
            })
            .filter(Boolean);

        if (selected.length > 0) {
            setProductosSeleccionados(selected);
            setPrefilled(true);
        } else {
            console.warn("[Pedido] Ningún ítem del pedido matcheó el catálogo; no se precarga.");
        }
    }, [clienteSelected?.marker, clienteSelected?.pedido, productos, prefilled, setProductosSeleccionados]);

    // Helpers de cantidades
    const getCantidad = useCallback(
        (productoId) => {
            const found = productosSeleccionados.find((p) => Number(p.id) === Number(productoId));
            return found ? Number(found.cantidad) || 0 : 0;
        },
        [productosSeleccionados]
    );

    const setCantidad = useCallback(
        (producto, cantidadNueva) => {
            const full = productos.find((x) => String(x.id) === String(producto.id)) || producto;
            const cantClamped = clampByStock(full, cantidadNueva);
            const triedMore = (Number(cantidadNueva) || 0) > cantClamped;

            setProductosSeleccionados((prev) => {
                const existe = prev.find((p) => p.id === full.id);
                if (!existe && cantClamped > 0) return [...prev, { ...full, cantidad: cantClamped }];
                if (existe && cantClamped > 0)
                    return prev.map((p) => (p.id === full.id ? { ...p, ...full, cantidad: cantClamped } : p));
                if (existe && cantClamped === 0) return prev.filter((p) => p.id !== full.id);
                return prev;
            });

            if (triedMore) {
                window.alert(
                    `Stock insuficiente. Solo hay ${Math.max(0, Math.floor(full.stock || 0))} unidades disponibles.`
                );
            }
        },
        [productos, setProductosSeleccionados]
    );

    const inc = (item) => setCantidad(item, getCantidad(item.id) + 1);
    const dec = (item) => setCantidad(item, getCantidad(item.id) - 1);

    // 4) Cálculos por combo
    const qtyByCombo = useMemo(() => buildQtyByCombo(productosSeleccionados), [productosSeleccionados]);

    // Handler para precio personalizado
    const setPrecioCustom = useCallback((productId, customPrice) => {
        setCustomPrices((prev) => {
            if (customPrice === null || customPrice === undefined || customPrice === "") {
                const newPrices = { ...prev };
                delete newPrices[productId];
                return newPrices;
            }
            return { ...prev, [productId]: Number(customPrice) };
        });
    }, []);

    // Función para obtener precio unitario (custom o calculado)
    const getUnitPrice = useCallback((item, cantidad) => {
        // Si hay precio custom, usarlo
        if (customPrices[item.id] !== undefined) {
            return Number(customPrices[item.id]);
        }
        // Sino, usar el calculado por combo
        return unitByCombo(item, cantidad, qtyByCombo);
    }, [customPrices, qtyByCombo]);

    const getComision = useCallback((item, cantidad) => {
        return comisionByCombo(item, cantidad, qtyByCombo);
    }, [qtyByCombo]);

    const total = useMemo(() => {
        return productosSeleccionados.reduce((acc, p) => {
            const q = Number(p.cantidad) || 0;
            const unit = getUnitPrice(p, q);
            return acc + (unit * q);
        }, 0);
    }, [productosSeleccionados, getUnitPrice]);

    // 5) Resumen
    const handleResumen = () => {
        const items = productosSeleccionados.map((p) => {
            const qty = Number(p.cantidad) || 0;
            const unitFinal = getUnitPrice(p, qty);
            const comisionPorc = getComision(p, qty);
            const subtotal = Number(unitFinal * qty);
            const comisionMonto = (subtotal * comisionPorc) / 100;
            return {
                id: p.id,
                descripcion: p.descripcion,
                cantidad: qty,
                numerocombo: p.numerocombo ?? null,
                precio_unitario_final: unitFinal,
                subtotal: subtotal,
                preciocosto: Number(p.preciocosto ?? 0),
                comision_porcentaje: comisionPorc,
                comision: comisionMonto,
            };
        });

        const payload = { cliente, items, total };
        if (typeof onResumen === "function") onResumen(payload);
        else {
            navigate("/resumen", {
                replace: true,
                state: { items }  // Pasar los items con precios personalizados
            });
            /*console.log("Resumen:", payload);
            window.alert("Resumen preparado. Revisá la consola.");*/
        }
    };

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <header className="sticky top-0 z-20 bg-white border-b border-gray-200">
                <div className="mx-auto max-w-5xl px-4 py-3 grid gap-3 md:flex md:items-center md:justify-between">
                    <div className="min-w-64">
                        <Select
                            options={clientes}
                            placeholder="Seleccione Cliente"
                            onChange={(value) => setClienteSelected(value)}
                            name="sCliente"
                        />
                    </div>
                    <div>
                        <p className="text-xs text-gray-500">Cliente</p>
                        <h1 className="text-lg font-semibold">{cliente?.nombre ?? "—"}</h1>
                    </div>
                </div>
            </header>

            {/* Lista de productos */}
            <main className="mx-auto max-w-5xl px-4 pb-32">
                {loading ? (
                    <div className="py-12 text-center text-gray-500">Cargando productos…</div>
                ) : (
                    <ul className="divide-y divide-gray-200">
                        {productos.map((item) => {
                            const cantidad = getCantidad(item.id);
                            const unitCalculado = unitByCombo(item, cantidad, qtyByCombo);
                            const unit = getUnitPrice(item, cantidad);
                            const subtotal = unit * cantidad;
                            const hint = nextComboHint(item, qtyByCombo);
                            const hasCustomPrice = customPrices[item.id] !== undefined;

                            return (
                                <li key={item.id} className="py-4 flex gap-4">
                                    <div className="flex-1">
                                        <div className="flex items-center justify-between">
                                            <h3 className="text-base font-medium text-gray-900">{item.descripcion}</h3>
                                            <div className="text-sm text-gray-500 flex items-center gap-3">
                                                {!!item.numerocombo && (
                                                    <span className="inline-flex items-center rounded-full bg-emerald-50 px-2 py-0.5 text-emerald-700 text-xs border border-emerald-200">
                                                        Combo #{item.numerocombo}
                                                    </span>
                                                )}
                                                <span>Stock: {Number(item.stock ?? 0)}</span>
                                            </div>
                                        </div>

                                        <div className="mt-2 flex items-center gap-2 text-sm">
                                            <span className="text-gray-600">Precio Unit.:</span>
                                            <input
                                                type="number"
                                                //step="01"
                                                className="h-8 w-28 rounded border border-gray-300 px-2 text-center text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                value={unit}
                                                onChange={(e) => {
                                                    const val = e.target.value;
                                                    if (val === "") {
                                                        setPrecioCustom(item.id, null);
                                                    } else {
                                                        const precio = parseFloat(val);
                                                        if (!isNaN(precio) && precio >= 0) {
                                                            setPrecioCustom(item.id, precio);
                                                        }
                                                    }
                                                }}
                                                placeholder={formatARS(unitCalculado)}
                                            />
                                            {hasCustomPrice && (
                                                <button
                                                    onClick={() => setPrecioCustom(item.id, null)}
                                                    className="text-xs text-blue-600 hover:text-blue-800 underline"
                                                    title="Restaurar precio automático"
                                                >
                                                    Restaurar
                                                </button>
                                            )}
                                            <span className="mx-2">•</span>
                                            <span className="text-gray-600">Subtotal:</span>
                                            <span className="font-semibold text-gray-900">{formatARS(subtotal)}</span>
                                        </div>

                                        {!!hint && !hasCustomPrice && (
                                            <div className="mt-1 text-xs text-emerald-700">
                                                💡 (combo) Te faltan <span className="font-semibold">{hint.faltan}</span> para{" "}
                                                <span className="font-semibold">{formatARS(hint.precio)}</span> c/u
                                            </div>
                                        )}

                                        <div className="mt-3 flex items-center gap-2">
                                            <button
                                                onClick={() => dec(item)}
                                                className="h-9 w-9 rounded-full bg-blue-600 text-white text-lg leading-none flex items-center justify-center hover:bg-blue-700"
                                                aria-label="Disminuir"
                                            >
                                                –
                                            </button>

                                            <input
                                                type="tel"
                                                inputMode="numeric"
                                                className="h-9 w-16 rounded-md border border-gray-300 text-center text-base shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                value={String(cantidad || 0)}
                                                onChange={(e) => {
                                                    const parsed = parseInt((e.target.value || "").replace(/[^\d]/g, ""), 10);
                                                    setCantidad(item, isNaN(parsed) ? 0 : parsed);
                                                }}
                                            />

                                            <button
                                                onClick={() => inc(item)}
                                                className="h-9 w-9 rounded-full bg-blue-600 text-white text-lg leading-none flex items-center justify-center hover:bg-blue-700"
                                                aria-label="Aumentar"
                                            >
                                                +
                                            </button>
                                        </div>
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </main>

            {/* Footer fijo */}
            <footer className="fixed bottom-0 left-0 right-0 z-30 border-t border-gray-200 bg-white/95 backdrop-blur">
                <div className="mx-auto max-w-5xl px-4 py-3 flex items-center gap-4">
                    <div className="flex-1">
                        <p className="text-xs text-gray-500 leading-none">Total</p>
                        <p className="text-xl font-semibold">{formatARS(total)}</p>
                    </div>
                    <button
                        disabled={
                            !(Array.isArray(productosSeleccionados) && productosSeleccionados.length) || total <= 0
                        }
                        onClick={handleResumen}
                        className="inline-flex items-center justify-center rounded-lg bg-gray-900 px-4 py-2 text-white font-medium disabled:opacity-50"
                    >
                        Ver resumen
                    </button>
                </div>
            </footer>
        </div>
    );
}
